import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    CalendarClock, ClipboardList, Download, FileText, Loader2, MessageSquare, Send,
    ShoppingBag, Stethoscope, Trash2, Upload, X,
} from "lucide-react";
import { SlotPicker } from "../components/SlotPicker";
import { StoryForm } from "../components/StoryForm";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { consultApi, consultError } from "../lib/consult";
import { startConsultPayment } from "../lib/cashfree";
import { formatINR } from "../lib/constants";
import { PageLoader } from "../components/LoadingRing";

export default function MyConsultationDetail() {
    const { bookingId } = useParams();
    const { customer, loading } = useCustomerAuth();
    const [state, setState] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [products, setProducts] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [reschedule, setReschedule] = useState(null);
    const [paying, setPaying] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        const { data } = await consultApi.get(`/me/bookings/${bookingId}`);
        setState(data);
    }, [bookingId]);

    const loadMessages = useCallback(async () => {
        try {
            const { data } = await consultApi.get(`/me/bookings/${bookingId}/messages`);
            setMessages(data);
        } catch { /* ignore */ }
    }, [bookingId]);

    useEffect(() => {
        if (!customer) return;
        load().catch((e) => toast.error(consultError(e)));
        loadMessages();
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)
            .then((r) => r.json()).then((d) => setProducts(d.filter((p) => p.price > 0))).catch(() => {});
    }, [customer, load, loadMessages]);

    useEffect(() => {
        const t = setInterval(loadMessages, 12000);
        return () => clearInterval(t);
    }, [loadMessages]);

    if (loading) return <PageLoader />;
    if (!customer) return <Navigate to="/account/login" replace />;
    if (!state) return <PageLoader label="LOADING CONSULTATION…" />;

    const b = state.booking;
    const docs = state.documents || [];

    const payNow = async () => {
        setPaying(true);
        try {
            sessionStorage.setItem("rhm_pending_consult", bookingId);
            await startConsultPayment(bookingId);
        } catch (e) {
            toast.error(consultError(e));
            setPaying(false);
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await consultApi.post(`/me/bookings/${bookingId}/documents`, fd);
            toast.success("Report uploaded securely");
            await load();
        } catch (e) { toast.error(consultError(e)); } finally { setUploading(false); }
    };

    const openDoc = async (d) => {
        try {
            const res = await consultApi.get(`/me/documents/${d.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { toast.error(consultError(e)); }
    };

    const removeDoc = async (d) => {
        try {
            await consultApi.delete(`/me/documents/${d.id}`);
            await load();
        } catch (e) { toast.error(consultError(e)); }
    };

    const send = async () => {
        if (!draft.trim()) return;
        try {
            await consultApi.post(`/me/bookings/${bookingId}/messages`, { body: draft });
            setDraft("");
            await loadMessages();
        } catch (e) { toast.error(consultError(e)); }
    };

    const doReschedule = async () => {
        try {
            await consultApi.post(`/me/bookings/${bookingId}/reschedule`, reschedule);
            toast.success("Appointment moved");
            setReschedule(null);
            await load();
        } catch (e) { toast.error(consultError(e)); }
    };

    const cancel = async () => {
        try {
            await consultApi.post(`/me/bookings/${bookingId}/cancel`);
            toast.success("Consultation cancelled");
            await load();
        } catch (e) { toast.error(consultError(e)); }
    };

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid="my-consultation-detail-page">
            <div className="mx-auto max-w-6xl px-6 md:px-12">
                <Link to="/account/consultations" className="mono text-[10px] tracking-[0.2em] text-zinc-500 hover:text-gold"
                    data-testid="detail-back-link">← MY CONSULTATIONS</Link>

                <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="mono text-[10px] tracking-[0.2em] text-gold">{b.consultation_id}</div>
                        <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase tracking-tight">{b.package_name}</h1>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-400">
                            <span className="inline-flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" /> {b.doctor_name}</span>
                            <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {b.slot_label} IST</span>
                            <span>{formatINR(b.amount)} · payment {b.payment_status}</span>
                        </div>
                    </div>
                    {b.status === "scheduled" && (
                        <div className="flex flex-wrap gap-3">
                            <button onClick={() => setReschedule({ slot_date: b.slot_date, slot_time: b.slot_time })}
                                data-testid="detail-reschedule-button" className="btn-pill btn-secondary">Reschedule</button>
                            <button onClick={cancel} data-testid="detail-cancel-button" className="btn-pill btn-secondary">Cancel</button>
                        </div>
                    )}
                </div>

                <div className="mt-10 grid lg:grid-cols-[1fr_300px] gap-8 items-start">
                    <div className="space-y-6">
                        {["pending", "failed"].includes(b.payment_status) && Number(b.amount || 0) > 0
                            && b.status !== "cancelled" && (
                            <div className="border border-gold/30 bg-gold/[0.06] p-6 flex flex-wrap items-center justify-between gap-4"
                                data-testid="detail-payment-due">
                                <div className="text-sm text-zinc-300">
                                    Payment of <span className="text-white">{formatINR(b.amount)}</span> is pending.
                                    Your slot stays held until you pay.
                                </div>
                                <button onClick={payNow} disabled={paying} data-testid="detail-pay-button"
                                    className="btn-pill btn-primary disabled:opacity-50">
                                    {paying ? "OPENING…" : "PAY NOW"}
                                </button>
                            </div>
                        )}
                        {/* intake */}
                        <div className="border border-white/10 bg-bg1 p-6" data-testid="detail-intake-card">
                            <div className="flex items-center justify-between gap-4">
                                <div className="font-display text-xl uppercase tracking-tight">Patient intake</div>
                                <span className="mono text-[9px] tracking-[0.16em] text-zinc-400">
                                    {(b.intake_status || "pending").toUpperCase()}
                                </span>
                            </div>
                            {b.intake_status === "submitted" ? (
                                <p className="mt-3 text-sm text-zinc-400">
                                    Submitted — your consultant has your answers. Reach out in the message thread if anything changed.
                                </p>
                            ) : (
                                <>
                                    <p className="mt-3 text-sm text-zinc-400">
                                        Complete your intake form so your consultant can prepare before the appointment.
                                    </p>
                                    <Link to={`/consultation/intake/${b.id}`} data-testid="detail-continue-intake"
                                        className="mt-5 inline-flex btn-pill btn-primary">
                                        <ClipboardList className="w-4 h-4 mr-2" /> Continue intake
                                    </Link>
                                </>
                            )}
                        </div>

                        {/* recommendations */}
                        {state.recommendations && (
                            <div className="border border-gold/30 bg-gold/[0.06] p-6" data-testid="detail-recommendations">
                                <div className="font-display text-xl uppercase tracking-tight">Your consultant's plan</div>
                                <p className="mt-3 text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{state.recommendations}</p>
                            </div>
                        )}

                        {/* documents */}
                        <div className="border border-white/10 bg-bg1 p-6" data-testid="detail-documents-card">
                            <div className="flex items-center justify-between gap-4">
                                <div className="font-display text-xl uppercase tracking-tight">Reports & documents</div>
                                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                                    data-testid="detail-upload-button" className="btn-pill btn-secondary">
                                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                    <span className="ml-2">Upload</span>
                                </button>
                                <input ref={fileRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
                                    data-testid="detail-file-input"
                                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
                            </div>
                            <p className="mt-2 text-xs text-zinc-500">JPG, PNG, WEBP, HEIC or PDF · up to 10 MB each</p>
                            {docs.length === 0 ? (
                                <p className="mt-5 text-sm text-zinc-500">No documents uploaded yet.</p>
                            ) : (
                                <ul className="mt-5 space-y-2">
                                    {docs.map((d) => (
                                        <li key={d.id} className="flex items-center gap-3 border border-white/10 px-4 py-3"
                                            data-testid={`detail-document-${d.id}`}>
                                            <FileText className="w-4 h-4 text-gold shrink-0" />
                                            <span className="text-sm text-zinc-200 truncate flex-1">{d.original_filename}</span>
                                            <button onClick={() => openDoc(d)} data-testid={`detail-doc-open-${d.id}`}
                                                className="text-zinc-400 hover:text-gold"><Download className="w-4 h-4" /></button>
                                            <button onClick={() => removeDoc(d)} data-testid={`detail-doc-delete-${d.id}`}
                                                className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <StoryForm bookingId={b.id} sensitive={state.area_sensitive} />

                        {/* chat */}
                        <div className="border border-white/10 bg-bg1 p-6" data-testid="detail-chat-card">
                            <div className="font-display text-xl uppercase tracking-tight flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-gold" /> Message your consultant
                            </div>
                            <div className="mt-5 space-y-3 max-h-[380px] overflow-y-auto pr-1" data-testid="detail-chat-thread">
                                {messages.length === 0 && <p className="text-sm text-zinc-500">No messages yet. Say hello.</p>}
                                {messages.map((m) => (
                                    <div key={m.id} className={`max-w-[85%] px-4 py-3 text-sm ${
                                        m.sender_type === "patient"
                                            ? "ml-auto bg-gold/10 border border-gold/25 text-zinc-100"
                                            : "bg-white/5 border border-white/10 text-zinc-200"
                                    }`}>
                                        <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">{m.sender_name.toUpperCase()}</div>
                                        <div className="mt-1.5 whitespace-pre-wrap">{m.body}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-5 flex gap-2">
                                <input value={draft} onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && send()} data-testid="detail-chat-input"
                                    placeholder="Type your message…"
                                    className="flex-1 bg-bg0 border border-white/12 text-white px-3 py-3 text-sm outline-none focus:border-gold placeholder:text-zinc-600" />
                                <button onClick={send} data-testid="detail-chat-send" className="btn-pill btn-primary">
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="mt-3 text-[11px] text-zinc-500">
                                Replies come within your plan's support window. For emergencies contact your nearest hospital.
                            </p>
                        </div>
                    </div>

                    {/* product sidebar */}
                    <aside className="lg:sticky lg:top-28 border border-white/10 bg-bg1 p-5" data-testid="detail-product-sidebar">
                        <div className="mono text-[10px] tracking-[0.22em] text-zinc-500 flex items-center gap-2">
                            <ShoppingBag className="w-3.5 h-3.5 text-gold" /> RECOMMENDED RANGE
                        </div>
                        <div className="mt-4 space-y-4">
                            {products.map((p) => (
                                <Link key={p.slug} to={`/product/${p.slug}`} data-testid={`sidebar-product-${p.slug}`}
                                    className="block group">
                                    {p.images?.[0] && (
                                        <div className="aspect-square bg-black/40 border border-white/10 overflow-hidden">
                                            <img src={p.images[0]} alt={p.name}
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    )}
                                    <div className="mt-3 font-display text-lg uppercase tracking-tight">{p.name}</div>
                                    <div className="text-xs text-zinc-400">{p.tagline}</div>
                                    <div className="mt-1 text-sm text-gold">{formatINR(p.price)}</div>
                                </Link>
                            ))}
                        </div>
                        <p className="mt-5 text-[11px] text-zinc-500 leading-relaxed">
                            Products are optional. Your consultant will advise only what is appropriate for you.
                        </p>
                    </aside>
                </div>
            </div>

            {/* reschedule modal */}
            {reschedule && (
                <div className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
                    data-testid="reschedule-modal" onClick={() => setReschedule(null)}>
                    <div className="w-full max-w-lg bg-bg1 border border-white/12 p-6 my-10" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-4">
                            <h3 className="font-display text-2xl uppercase tracking-tight">Move appointment</h3>
                            <button onClick={() => setReschedule(null)} data-testid="reschedule-close"
                                className="w-9 h-9 border border-white/15 flex items-center justify-center text-zinc-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="mt-6">
                            <SlotPicker doctorId={b.doctor_id} value={reschedule} onChange={setReschedule} testPrefix="reschedule-slot" />
                        </div>
                        <button onClick={doReschedule} data-testid="reschedule-confirm"
                            className="mt-7 w-full btn-pill btn-primary justify-center">Confirm new time</button>
                    </div>
                </div>
            )}
        </div>
    );
}
